// The one way this platform names a team.
//
// ⚠️ LEAF MODULE. It must never import from './index'. `validatePrefsInput`'s
// move records why: re-exporting a constant through `index` closes an init
// cycle whose only symptom is an undefined constant inside a formatter.
//
// ⚠️ The separator is the BULLET, never " - ". One real Fall 2026 team name is
// `Rao - Asteroids`, which under a hyphen separator renders as four segments
// where three were meant. Owner ruling 2026-08-29.

export const TEAM_LABEL_SEPARATOR = ' · '

/** PURE. "John Smith" -> "Smith, John".
 *
 *  The last whitespace token is the surname; everything before it is the given
 *  name. A single token is returned unchanged. Inverting it would invent a
 *  comma. Blank or absent input returns null so callers can drop the segment.
 *
 *  ⚠️ Inverted deliberately, everywhere a coach is named (owner ruling
 *  2026-08-29). The region has coaches who share a surname, so a surname alone
 *  does not identify one. */
export function coachSortName(full: string | null | undefined): string | null {
  const parts = (full ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return null
  if (parts.length === 1) return parts[0]!
  const surname = parts[parts.length - 1]!
  return `${surname}, ${parts.slice(0, -1).join(' ')}`
}

export interface TeamLabelInput {
  designation: string | null | undefined
  coachName: string | null | undefined
  teamName: string | null | undefined
}

/** PURE. "<designation> · <Coach, First> · <Team name>", each segment dropped
 *  when absent. The designation prints as stored (`B12-01`, InLeague's form
 *  since #881), on every surface: one spelling of a team id everywhere.
 *
 *  Returns '' when there is no designation. Inventing one would be worse than
 *  an empty label: `external_id` is the team's identity to every read site, and
 *  this codebase already refuses to mint a team label InLeague did not give
 *  (see `teamExternalId`'s skip-and-warn on a missing teamLetter).
 *
 *  ⚠️ A single object argument, not three positionals. The helper this replaces
 *  took (name, externalId, coach), where name was FIRST (the opposite of the
 *  rendered order). Two adjacent `string | null` positions are one careless swap
 *  from a silently wrong label that nothing type-checks. */
export function teamLabel(input: TeamLabelInput): string {
  const designation = (input.designation ?? '').trim()
  if (!designation) return ''

  const segments: string[] = [designation]
  const coach = coachSortName(input.coachName)
  if (coach) segments.push(coach)
  const name = (input.teamName ?? '').trim()
  if (name) segments.push(name)

  return segments.join(TEAM_LABEL_SEPARATOR)
}

/** The shared case table. `teamLabel.test.ts` walks it, and so does the Python
 *  twin's parity test (`web/container/tests/test_team_label_parity.py`) via
 *  `web/packages/shared-types/src/teamLabelCases.json`. Two implementations of one rule is the
 *  failure this repo keeps recording; this table is what stops them drifting. */
export const TEAM_LABEL_CASES = [
  { designation: 'B12-01', coachName: 'John Smith', teamName: 'Tigers', expected: 'B12-01 · Smith, John · Tigers' },
  { designation: 'B12-01', coachName: 'John Smith', teamName: null, expected: 'B12-01 · Smith, John' },
  { designation: 'B12-01', coachName: 'John Smith', teamName: '   ', expected: 'B12-01 · Smith, John' },
  { designation: 'B12-01', coachName: null, teamName: 'Tigers', expected: 'B12-01 · Tigers' },
  { designation: 'B12-01', coachName: null, teamName: null, expected: 'B12-01' },
  { designation: 'B12-01', coachName: '  ', teamName: null, expected: 'B12-01' },
  { designation: 'G14-01', coachName: 'Pelé', teamName: null, expected: 'G14-01 · Pelé' },
  { designation: 'B10-03', coachName: 'Maria de la Cruz', teamName: null, expected: 'B10-03 · Cruz, Maria de la' },
  { designation: 'B14-09', coachName: 'Anil Rao', teamName: 'Rao - Asteroids', expected: 'B14-09 · Rao, Anil · Rao - Asteroids' },
  { designation: null, coachName: 'John Smith', teamName: 'Tigers', expected: '' },
  { designation: '', coachName: null, teamName: null, expected: '' },
  { designation: '', coachName: 'John Smith', teamName: 'Tigers', expected: '' },
  { designation: 'B12-01', coachName: '  John   Smith  ', teamName: null, expected: 'B12-01 · Smith, John' },
  { designation: 'B10-X', coachName: 'Ana Kline', teamName: null, expected: 'B10-X · Kline, Ana' },
  { designation: 'B12-01', coachName: null, teamName: null, expected: 'B12-01' }
] as const satisfies readonly TeamLabelCase[]

export interface TeamLabelCase {
  designation: string | null
  coachName: string | null
  teamName: string | null
  expected: string
}
